import React, { type Ref } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ENTITY_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import type { JiraLinkConfig } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import FilterIcon from '@atlaskit/icon/core/filter';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useNestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop/nested-item-drag-and-drop';
import { Nav4FilterActions } from '../filter-actions/Nav4FilterActions.tsx';
import { useFilterReorderingIds } from '../../utils/use-filter-reordering-id.tsx';
import { FILTER_HREF } from '../../constants.tsx';
import { Nav4FilterActionsRefactored } from '../filter-actions/Nav4FilterActionsRefactored.tsx';
import type { Nav4FilterMenuItemRefactored$key } from './__generated__/Nav4FilterMenuItemRefactored.graphql.ts';

type Nav4FilterMenuItemRefactoredProps = {
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
	queryRef: Nav4FilterMenuItemRefactored$key;
	onRefetch: (options: { onComplete: () => void }) => void;
	onClick: () => void;
};

export function Nav4FilterMenuItemRefactored(
	props: Nav4FilterMenuItemRefactoredProps,
): React.JSX.Element {
	const { queryRef, currentItems, currentOrder, item, onClick, onRefetch } = props;
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4FilterMenuItemRefactored$key>(
		graphql`
			fragment Nav4FilterMenuItemRefactored on JiraFilter {
				filterId
				id
				isFavourite
				name
				...Nav4FilterActions
				...Nav4FilterActionsRefactored
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const isAnonymous = useIsAnonymous();

	const { filterId, id, isFavourite, name } = data;

	if (isFavourite && currentItems && currentOrder && item) {
		const actions = (
			<Nav4FilterActionsRefactored
				queryRef={data}
				onRefetch={onRefetch}
				currentItems={currentItems}
				currentOrder={currentOrder}
				item={item}
			/>
		);
		return (
			<DraggableFilterMenuItem
				actionsOnHover={actions}
				filterId={filterId}
				id={id}
				isStarred={isFavourite}
				filterTitle={name}
				onClick={onClick}
			/>
		);
	}

	return (
		<SimpleFilterMenuItem
			actionsOnHover={!isAnonymous && <Nav4FilterActions queryRef={data} onRefetch={onRefetch} />}
			filterId={data.filterId}
			name={data.name}
			onClick={onClick}
		/>
	);
}

type SimpleFilterMenuItemProps = {
	actionsOnHover?: React.ReactNode;
	filterId: string;
	name: string;
	onClick: () => void;
};

export function SimpleFilterMenuItem({
	actionsOnHover,
	filterId,
	name,
	onClick,
}: SimpleFilterMenuItemProps): React.JSX.Element {
	return (
		<Nav4MenuLinkItem<JiraLinkConfig>
			actionsOnHover={actionsOnHover}
			elemBefore={<FilterIcon label="" color="currentColor" />}
			href={FILTER_HREF(filterId)}
			menuId={ENTITY_ID.FILTER(filterId)}
			onClick={onClick}
		>
			{name}
		</Nav4MenuLinkItem>
	);
}

type DraggableFilterMenuItemProps = {
	actionsOnHover: React.ReactNode;
	id: string;
	isStarred: boolean;
	filterId: string;
	filterTitle: string;
	onClick: () => void;
};

const DraggableFilterMenuItem = ({
	actionsOnHover,
	id,
	filterId,
	filterTitle,
	onClick,
}: DraggableFilterMenuItemProps) => {
	const { entityAri, ownerAri, group } = useFilterReorderingIds();
	const dragAndDropState = useNestedItemDragAndDrop({
		entityAri,
		ownerAri,
		data: {
			id: id.toString(),
			label: filterTitle,
		},
		group,
		item: undefined,
		isExpandable: false,
		isExpandOrFlyoutOpen: false,
		id,
		isDragAndDropEnabled: true,
		onExpandFlyoutToggle: () => {
			// No op for dashboard menu item
		},
	});

	const draggableRef: Ref<HTMLAnchorElement> = dragAndDropState?.draggableRef || null;

	return (
		<>
			<Nav4MenuLinkItem<JiraLinkConfig>
				// analytics={analytics}
				elemBefore={<FilterIcon label="" color="currentColor" />}
				href={FILTER_HREF(filterId)}
				menuId={ENTITY_ID.FILTER(filterId)}
				ref={draggableRef}
				actionsOnHover={actionsOnHover}
				visualContentRef={dragAndDropState?.dropTargetRef}
				dropIndicator={dragAndDropState?.dropIndicator}
				isDragging={dragAndDropState?.isDragging}
				hasDragIndicator
				onClick={onClick}
			>
				{filterTitle}
			</Nav4MenuLinkItem>
			{dragAndDropState && dragAndDropState.maybeDragPreview}
		</>
	);
};
